import { useCallback } from 'react';
import type { DataTableMode } from '@odo/shared-ui';
import { useUrlArrayParam } from '../../../hooks/useUrlArrayParam';
import type { DmInboxStatus, DmNotificationSort } from '../../../services/dmNotifications';
import { parseInboxStatus, sortForStatus } from '../shared/inboxStatus';
import { parseInboxSearch } from '../shared/listRouting';
import { parseFoldMode, type FoldMode } from './inboxFolds';
import {
    CLOSED_SORT_RULE,
    LIST_PARAM,
    listParamValue,
    parseMode,
    parseOrder,
    parseSort,
    pickNotificationListSearch
} from './notificationListParams';
import {
    nextSortOrder,
    usePageForSignature,
    useUpdateListParams,
    type ListParamUpdates,
    type ListSortOrder
} from '../shared/listUrlState';

export type NotificationListFilterName = 'levels' | 'practiceIds' | 'assignees' | 'tags';

// Highest level, newest and longest waiting (oldest first) lead on a first click.
const FIRST_CLICK_ORDER: Record<DmNotificationSort, ListSortOrder> = {
    level: 'desc',
    latest: 'desc',
    waiting: 'asc'
};

// The notifications list's table state, with the URL as the single source of truth.
export function useNotificationListUrlState(pageSize: number) {
    const { searchParams, updateParams } = useUpdateListParams();
    const [levels, setLevels] = useUrlArrayParam(LIST_PARAM.levels, searchParams, updateParams);
    const [practiceIds, setPracticeIds] = useUrlArrayParam(
        LIST_PARAM.practiceIds,
        searchParams,
        updateParams
    );
    const [assignees, setAssignees] = useUrlArrayParam(
        LIST_PARAM.assignees,
        searchParams,
        updateParams
    );
    const [tags, setTags] = useUrlArrayParam(LIST_PARAM.tags, searchParams, updateParams);
    const search = parseInboxSearch(searchParams.get(LIST_PARAM.search));
    const status = parseInboxStatus(searchParams.get(LIST_PARAM.status));
    const rows = parseFoldMode(searchParams.get(LIST_PARAM.rows));
    const sort = parseSort(searchParams.get(LIST_PARAM.sort), status);
    const order = parseOrder(searchParams.get(LIST_PARAM.order));
    const mode = parseMode(searchParams.get(LIST_PARAM.mode));
    const signature = [search, status, sort, order, mode, levels, practiceIds, assignees, tags];
    const { page, setPage } = usePageForSignature(signature.join('|'));

    const filterSetters: Record<NotificationListFilterName, (value: string[]) => void> = {
        levels: setLevels,
        practiceIds: setPracticeIds,
        assignees: setAssignees,
        tags: setTags
    };
    const setSearch = useCallback(
        (value: string) => updateParams({ [LIST_PARAM.search]: listParamValue('search', value) }),
        [updateParams]
    );
    const toggleSort = (by: DmNotificationSort) => {
        const nextOrder = nextSortOrder(by, { sort, order }, FIRST_CLICK_ORDER);
        updateParams({
            [LIST_PARAM.sort]: listParamValue('sort', by),
            [LIST_PARAM.order]: listParamValue('order', nextOrder)
        });
    };
    const setMode = (next: DataTableMode) =>
        updateParams({ [LIST_PARAM.mode]: listParamValue('mode', next) });
    const setStatus = (next: DmInboxStatus) => {
        const nextSort = sortForStatus(sort, next, CLOSED_SORT_RULE);
        const sortReset: ListParamUpdates =
            nextSort === sort
                ? {}
                : { [LIST_PARAM.sort]: listParamValue('sort', nextSort), [LIST_PARAM.order]: null };
        updateParams({ [LIST_PARAM.status]: listParamValue('status', next), ...sortReset });
    };
    const setRows = (next: FoldMode) =>
        updateParams({ [LIST_PARAM.rows]: listParamValue('rows', next) });
    const clearFilters = () =>
        updateParams({
            [LIST_PARAM.search]: null,
            [LIST_PARAM.levels]: null,
            [LIST_PARAM.practiceIds]: null,
            [LIST_PARAM.assignees]: null,
            [LIST_PARAM.tags]: null
        });

    return {
        page,
        setPage,
        limit: pageSize,
        search,
        setSearch,
        sort,
        order,
        toggleSort,
        mode,
        setMode,
        status,
        setStatus,
        rows,
        setRows,
        filters: { levels, practiceIds, assignees, tags },
        setFilter: (name: NotificationListFilterName, value: string[]) =>
            filterSetters[name](value),
        clearFilters,
        listSearch: pickNotificationListSearch(searchParams)
    };
}
